import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import { identifyUser, registerSuperProperties, resetUser as resetAnalyticsUser, trackEvent } from '../services/analytics'
import type { AnalyticsUserProperties } from '../types/analytics'
import type { UserProfile } from '../services/api'
import { authCallback, getMe } from '../services/api'

export const useUserStore = defineStore('user', () => {
    // State
    const user = ref<UserProfile | null>(null)
    const isLoading = ref(false)
    const needsSignup = ref(false)
    const error = ref<string | null>(null)

    // Computed
    const isAuthenticated = computed(() => user.value !== null)
    const role = computed(() => user.value?.role ?? null)
    const isAdmin = computed(() => user.value?.role === 'ADMIN')
    const isAdvisor = computed(() => user.value?.role === 'ADVISOR')
    const isSupplier = computed(() =>
        user.value?.role === 'SUPPLIER_ADMIN' || user.value?.role === 'SUPPLIER_USER'
    )
    const isSupplierAdmin = computed(() => user.value?.role === 'SUPPLIER_ADMIN')
    const isSupplierUser = computed(() => user.value?.role === 'SUPPLIER_USER')

    // Get the relevant ID for API calls based on role
    const advisorId = computed(() => user.value?.advisor?.id ?? null)
    const supplierId = computed(() => user.value?.supplierEmployee?.supplier?.id ?? null)
    const supplierEmployeeId = computed(() => user.value?.supplierEmployee?.id ?? null)

    // Tier info
    const advisorTier = computed(() => user.value?.advisor?.tier ?? null)
    const supplierTier = computed(() => user.value?.supplierEmployee?.supplier?.tier ?? null)

    // Advisor's TSD network names (preserve original casing from DB)
    const advisorTsdNames = computed(() =>
        (user.value?.advisor?.tsdNames ?? [])
    )

    // Actions
    async function initUser(token: string, email: string, name: string | null) {
        isLoading.value = true
        error.value = null
        needsSignup.value = false

        try {
            const response = await authCallback(email, name, token)

            if (response.needsSignup) {
                needsSignup.value = true
                user.value = null
                return { needsSignup: true }
            }

            if (response.user) {
                user.value = response.user
                Sentry.setUser({ id: response.user.id, email: response.user.email })
                Sentry.setTag('user.role', response.user.role)
                _identifyAndRegister(response.user)
                return { user: response.user }
            }

            throw new Error('Unexpected response from auth callback')
        } catch (e: any) {
            error.value = e.message || 'Failed to initialize user'
            throw e
        } finally {
            isLoading.value = false
        }
    }

    async function fetchUser(token: string) {
        isLoading.value = true
        error.value = null

        try {
            const response = await getMe(token)

            if (response.needsSignup) {
                needsSignup.value = true
                user.value = null
                return { needsSignup: true }
            }

            if (response.user) {
                user.value = response.user
                Sentry.setUser({ id: response.user.id, email: response.user.email })
                Sentry.setTag('user.role', response.user.role)
                _identifyAndRegister(response.user)
                return { user: response.user }
            }

            throw new Error('Unexpected response from get me')
        } catch (e: any) {
            error.value = e.message || 'Failed to fetch user'
            throw e
        } finally {
            isLoading.value = false
        }
    }

    function setUser(newUser: UserProfile) {
        user.value = newUser
        needsSignup.value = false
        error.value = null
        Sentry.setUser({ id: newUser.id, email: newUser.email })
        Sentry.setTag('user.role', newUser.role)
        _identifyAndRegister(newUser)
    }

    function clearUser() {
        trackEvent('logout')
        user.value = null
        needsSignup.value = false
        error.value = null
        Sentry.setUser(null)
        resetAnalyticsUser()
    }

    /** Identify user in MixPanel and register super properties for all subsequent events. */
    function _identifyAndRegister(u: UserProfile) {
        const tier = u.supplierEmployee?.supplier?.tier ?? u.advisor?.tier ?? null
        const companyName = u.supplierEmployee?.supplier?.name ?? u.advisor?.firmName ?? null
        const onboardingCompleted = !!(
            u.advisor?.onboardingCompletedAt ??
            u.supplierEmployee?.supplier?.onboardingCompletedAt
        )

        const userProps: AnalyticsUserProperties = {
            $email: u.email,
            $name: u.name ?? null,
            role: u.role,
            tier,
            company_name: companyName,
            signup_date: u.createdAt ?? null,
            onboarding_completed: onboardingCompleted,
        }

        identifyUser(u.id, userProps)

        registerSuperProperties({
            role: u.role,
            tier,
            is_impersonating: !!originalToken.value,
            app_version: __APP_VERSION__,
        })
    }

    function getDefaultRoute(): string {
        if (!user.value) return '/'

        switch (user.value.role) {
            case 'ADMIN':
                return '/admin'
            case 'ADVISOR':
                return '/advisor/today'
            case 'SUPPLIER_ADMIN':
            case 'SUPPLIER_USER':
                return '/supplier'
            default:
                return '/'
        }
    }

    /**
     * Determine where to route the user after login/signup.
     * If onboarding is incomplete, route to the onboarding flow.
     * Otherwise, route to the default dashboard.
     */
    function getOnboardingRoute(): string {
        if (!user.value) return '/'

        if (user.value.role === 'ADVISOR' && user.value.advisor) {
            const { onboardingCompletedAt, onboardingSkippedAt, onboardingStep } = user.value.advisor
            if (!onboardingCompletedAt && !onboardingSkippedAt && onboardingStep) {
                return '/advisor/onboarding'
            }
        }

        if (user.value.role === 'SUPPLIER_ADMIN' && user.value.supplierEmployee?.supplier) {
            const { onboardingCompletedAt, onboardingSkippedAt, onboardingStep } = user.value.supplierEmployee.supplier
            if (!onboardingCompletedAt && !onboardingSkippedAt && onboardingStep) {
                return '/supplier/onboarding'
            }
        }

        return getDefaultRoute()
    }

    // Impersonation state
    const originalToken = ref<string | null>(null)
    const isImpersonating = computed(() => !!originalToken.value)

    function startImpersonation(adminToken: string, impToken: string, impersonatedUser: any) {
        // Save the admin's original token so we can restore later
        originalToken.value = adminToken
        localStorage.setItem('impersonation_original_token', adminToken)

        // Store the impersonation token for API calls
        localStorage.setItem('impersonation_token', impToken)

        // Update user state immediately
        user.value = impersonatedUser
    }

    async function stopImpersonation() {
        if (!originalToken.value) return

        trackEvent('admin_impersonation_stopped')

        const token = originalToken.value
        originalToken.value = null

        // Clean up
        localStorage.removeItem('impersonation_original_token')
        localStorage.removeItem('impersonation_token')

        // Re-fetch original user
        await fetchUser(token)

        // Force reload to ensure clean state
        window.location.href = '/admin'
    }

    return {
        // State
        user,
        isLoading,
        needsSignup,
        error,
        isImpersonating,

        // Computed
        isAuthenticated,
        role,
        isAdmin,
        isAdvisor,
        isSupplier,
        isSupplierAdmin,
        isSupplierUser,
        advisorId,
        supplierId,
        supplierEmployeeId,
        advisorTier,
        supplierTier,
        advisorTsdNames,

        // Actions
        initUser,
        fetchUser,
        setUser,
        clearUser,
        getDefaultRoute,
        getOnboardingRoute,
        startImpersonation,
        stopImpersonation
    }
})
